// Loads a list of top level items from the database and shows them.
// All Items that don't have a parentId associated with them.
// If you choose a top level item, loads the Item page, with the ID of that item passed to the Item page. 

import React, { useCallback, useState } from 'react';
import Table from 'react-bootstrap/Table';
import Button from 'react-bootstrap/Button';
import { saveEditedItem, createItem, NewItemName, client } from '../../utilities/Database';
import { useStatusMessage, DataCard, EmptyState, ModalEditForm, ItemsHelp } from '../../components';
import { itemFields } from '../../fleet-shared/ItemFields';
import { isHiddenItem, visibilityValue } from '../../fleet-shared/ItemVisibility';
import { CardHeader } from '../../components/CardHeader';
import { useMyInfiniteScroll } from '../../components/MyInfiniteScroll';
import { ItemRow } from './ItemRow';
import ItemsSettingsModal from './ItemsSettingsModal';
import { primeKeyboard, blurPrimer } from '../../utilities/keyboardPrimer';
import { validateItem } from '../../utilities/Validators.mjs';

const listTaskPogoItems = /* GraphQL */ `
  query ListTaskPogoItems(
    $filter: ModelTaskPogoItemFilterInput
    $limit: Int
    $nextToken: String
  ) {
    listTaskPogoItems(filter: $filter, limit: $limit, nextToken: $nextToken) {
      items {
        id
        name
        itemType
        tracking
        visibility
        soonDays
        soonCounter
      }
      nextToken
    }
  }
`;

// Page of Items to select. Shows status for each item (# of tasks coming due, etc.)
const Items = ( { user } ) => {

  // Edit Item UI.  When to show it, and what to show.
  const [showEditItem, setShowEditItem] = React.useState(false);
  const [editItem, setEditItem] = React.useState(null);

  // The Settings dialog: which items this page lists.
  const [showSettings, setShowSettings] = useState(false);
  const openSettings = useCallback(() => setShowSettings(true), []);

  // Loading/error UI
  const [StatusMessage, {showText, showError, hideStatus}] = useStatusMessage();

  const fetchItems = useCallback(async (token) => {
    const filter = {
      or: [
        {parentId: { attributeExists: false } },
        { parentId: { eq: null } }
      ]
    };

    const itemsData = await client.graphql({ query: listTaskPogoItems, variables: { filter: filter, nextToken: token }});
    const items = itemsData.data.listTaskPogoItems.items;
    const nextToken = itemsData.data.listTaskPogoItems.nextToken;

    return { items, nextToken };
  }, []);

  // Hidden items are filtered HERE, at render, not in fetchItems. `entries`
  // keeps every row the server sent, so the Settings dialog can flip one in
  // place -- and a page that filtered to nothing would hand MyInfiniteScroll
  // an empty page with a nextToken, which its scroll-driven fetch does not
  // re-request. With every loaded row hidden, the note renders INSIDE the
  // scroll control (not in place of it) so the pager stays mounted. Known
  // edge: a FULL page that is all hidden leaves no scroll region for the
  // pager to fire from, so items on later pages wait for the next open of
  // Settings -- which lists them regardless, and is the button offered here.
  const renderChildren = useCallback((items) => {
    const visible = items.filter((item) => !isHiddenItem(item));
    if (items.length > 0 && visible.length === 0) {
      return (
        <EmptyState
          icon="eye-slash"
          title="All your items are hidden"
          subtitle="Use Settings to choose which items this page lists."
          action={<Button variant="outline-secondary" onClick={openSettings}>Show hidden items</Button>}
        />
      );
    }
    return (
      <Table responsive hover className="align-middle mb-0 items-table">
        <tbody>
          {visible.map((item) =>
            <ItemRow
              key={item.id}
              identityId={user && user.identityId}
              itemId={item.id}
              name={item.name}
              itemType={item.itemType}
              tracking={item.tracking}
              soonDays={item.soonDays}
              soonCounter={item.soonCounter}
            />
          )}
        </tbody>
      </Table>
    );
  }, [user, openSettings]);

  const [entries, setEntries] = useState(null);

  const { Control } = useMyInfiniteScroll(
    renderChildren, 
    fetchItems,
    entries,
    setEntries,
    null
  );


  // Passed to EditItem. Called once per autosave flush with the edited
  // fields; must NOT close the modal (the user may still be editing).
  // throws on errors.
  const saveItem = useCallback(async (newItem) => {
    // Go do the update -- can throw!
    const savedItem = await saveEditedItem(newItem, editItem);

    // Update the UI. Must go through setEntries (not an in-place array
    // mutation): with autosave the save usually lands AFTER the modal-close
    // re-render, so there is no later render to piggyback on -- an in-place
    // write would leave the list showing the stale name.
    setEntries((prev) => {
      const list = prev ?? [];
      const index = list.findIndex((entry) => entry.id === savedItem.id);
      if (index < 0) {
        return [...list, savedItem];
      }
      const next = [...list];
      next[index] = savedItem;
      return next;
    });
  }, [editItem, setEntries]);


  // The Settings dialog changed an item's visibility (optimistically, and
  // again on a failed save's revert). Same setEntries-updater rule as saveItem.
  // A row not in the list yet is APPENDED: the list holds a prefix of the
  // items, and an item unhidden from beyond it would otherwise not appear
  // until reload -- mergeEntriesById dedupes it if its page arrives later.
  const handleVisibilityChange = useCallback((item, hidden) => {
    const visibility = visibilityValue(hidden);
    setEntries((prev) => {
      const list = prev ?? [];
      const index = list.findIndex((entry) => entry.id === item.id);
      if (index < 0) {
        return [...list, { ...item, visibility }];
      }
      const next = [...list];
      next[index] = { ...list[index], visibility };
      return next;
    });
  }, [setEntries]);

  // User tapped add item button.
  const addItem = useCallback(async () => {
    // Synchronously focus the hidden primer input INSIDE the tap gesture so
    // mobile browsers open the soft keyboard now. The real Name input takes
    // focus from the primer once the edit modal finishes animating in (see
    // ModalEditForm.onEntered). Without this, iOS Safari refuses to open
    // the keyboard after the `await createItem` below.
    primeKeyboard();

    showText("Creating...");

    try {
      // Add item to the database
      let newItem = {
        name: NewItemName,
      };
      // Note: this can throw (e.g. network or security error)
      const createdItem = await createItem(newItem);

      // Put up UI to edit the new item
      setEditItem(createdItem);
      setShowEditItem(true);
      hideStatus();
    } catch(err) {
      blurPrimer();
      showError(err);
    }
  }, [hideStatus, showError, showText]);

  // TODO: Below I do user && user.identityId. I seem to lose "user" on a fault. I think
  // I should redirect to home???
  return (
    <>
      <StatusMessage/>
      
      <DataCard
        header = {
          <CardHeader
            text='Items'
            addTapped={addItem}
            extraActions={
              <Button variant="outline-secondary" onClick={openSettings}>
                <i className="fa-solid fa-gear me-1" aria-hidden="true"></i>
                Settings
              </Button>
            }
          />
        }
        body = {
          // Loaded-and-empty gets guidance instead of a bare table. `entries`
          // is null until the first fetch lands (and reset to null on a fetch
          // error), so the swap neither flashes during the load nor hides the
          // Control-rendered error message; the fetch itself runs in the
          // hook's own effect, not in Control's mount, so skipping Control
          // here cannot stall it.
          entries?.length === 0 ? (
            <EmptyState
              icon="ship"
              title="No items yet"
              subtitle="Add a boat, plane, vehicle, or home to track its tasks — or just its trips."
              action={<Button variant="success" onClick={addItem}>Add an item</Button>}
            />
          ) : (
            <Control/>
          )
        }
      />

      <ItemsHelp/>

      <ItemsSettingsModal
        show={showSettings}
        onHide={() => setShowSettings(false)}
        onVisibilityChange={handleVisibilityChange}
      />

      <ModalEditForm
        show={showEditItem}
        fields={itemFields}
        onHide={() => setShowEditItem(false)}
        item={editItem}
        save={saveItem}
        validate={validateItem}
        recordKeyPrefix="item"
      />
    </>
  )
}  

export default Items

